import { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { useDriversSearch } from "@/hooks/use-drivers-search";
import { usePersistedState } from "@/lib/use-persisted-state";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { SiteHeader } from "@/components/site-header";
import { api, authorizedFetch } from "@/lib/api";
import { API_URL } from "@/lib/mutation-utils";
import { usePermissions } from "@/hooks/use-permissions";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogClose,
} from "@/components/ui/dialog";
import {
  Sheet, SheetContent, SheetDescription, SheetFooter,
  SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { SearchableSelect } from "@/components/ui/searchable-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  SearchIcon, PlusIcon, PencilIcon, Trash2Icon,
  RotateCcwIcon as RestoreIcon,
  ExternalLinkIcon, Share2Icon, CopyIcon, CheckIcon,
  FlameIcon, LeafIcon, DownloadIcon,
} from "lucide-react";
import { Pagination } from "@/components/ui/pagination";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { avatarInitials, avatarColor } from "@/lib/avatar";

// ─── Helpers ─────────────────────────────────────────────────────────────────

const DRIVER_STATUS_LABELS: Record<string, string> = {
  active:       "Activo",
  sick:         "Enfermo / de baja",
  on_vacation:  "De vacaciones",
  art:          "ART",
  suspended:    "Suspendido",
};

const UNIT_STATUS_LABELS: Record<string, string> = {
  active:       "Activo",
  in_repair:    "En reparación",
  damaged:      "Dañado",
  missing_docs: "Documentación incompleta",
};

function inactiveDotReason(label: string, deletedAt: string | null): string {
  return deletedAt ? `${label} eliminado del sistema` : `${label} inactivo`;
}

function mobileStatus(m: MobileRow): 'activo' | 'alistado' | 'incompleto' {
  if (m.isActive) return 'activo';
  // Truck assigned and active, but no driver → "ready" truck waiting for a driver
  if (!m.driverId && m.truckId && !m.truckDeletedAt && m.truckStatus === 'active') return 'alistado';
  return 'incompleto';
}

function mobileInactiveReasons(m: MobileRow): string {
  const reasons: string[] = [];
  if (!m.driverId)                                           reasons.push("Sin chofer asignado");
  else if (m.driverDeletedAt)                                reasons.push("Chofer eliminado");
  else if (m.driverStatus !== "active")                      reasons.push("Chofer inactivo");
  if (!m.truckId)                                            reasons.push("Sin camión asignado");
  else if (m.truckDeletedAt)                                 reasons.push("Camión eliminado");
  else if (m.truckStatus !== "active")                       reasons.push("Camión inactivo");
  if (!m.trailerId)                                          reasons.push("Sin semi asignado");
  else if (m.trailerDeletedAt)                               reasons.push("Semi eliminado");
  else if (m.trailerStatus !== "active")                     reasons.push("Semi inactivo");
  return reasons.join(" · ");
}

// ─── Types ────────────────────────────────────────────────────────────────────

const FLEETS = ["kaizen", "transjoven", "cdc"] as const;
type Fleet = typeof FLEETS[number];

const FLEET_LABELS: Record<Fleet, string> = {
  kaizen:     "Kaizen",
  transjoven: "Transjoven",
  cdc:        "CDC",
};

const FLEET_COLORS: Record<Fleet, string> = {
  kaizen:     "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
  transjoven: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400",
  cdc:        "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
};

const WORK_LINES = ["nacional", "internacional", "batea"] as const;
type WorkLine = typeof WORK_LINES[number];

const WORK_LINE_LABELS: Record<WorkLine, string> = {
  nacional:      "Nacional",
  internacional: "Internacional",
  batea:         "Batea",
};

const WORK_LINE_COLORS: Record<WorkLine, string> = {
  nacional:      "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  internacional: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
  batea:         "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
};

interface MobileRow {
  id: string;
  fleet: Fleet | null;
  workLines: WorkLine[];
  isActive: boolean; // computed
  notes: string | null;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  operatorId: string | null;
  operatorName: string | null;
  driverId: string | null;
  driverName: string | null;
  driverCode: string | null;
  driverDni: string | null;
  driverPhoneCdc: string | null;
  driverPhonePersonal: string | null;
  driverStatus: string | null;
  driverDeletedAt: string | null;
  truckId: string | null;
  truckCode: string | null;
  truckUnitNumber: number | null;
  truckLicensePlate: string | null;
  truckBrand: string | null;
  truckModel: string | null;
  truckManufactureYear: number | null;
  truckTypeCode: string | null;
  truckTypeName: string | null;
  truckStatus: string | null;
  truckDeletedAt: string | null;
  truckFuelType: 'diesel' | 'gnc' | null;
  trailerId: string | null;
  trailerCode: string | null;
  trailerLicensePlate: string | null;
  trailerBrand: string | null;
  trailerTypeCode: string | null;
  trailerTypeName: string | null;
  trailerStatus: string | null;
  trailerDeletedAt: string | null;
  trailerRefrigerationUnitId: string | null;
  trailerRefrigerationUnitCode: string | null;
  trailerRefrigerationUnitName: string | null;
  trailerRefrigerationUnitStatus: string | null;
  trailerManufactureYear: number | null;
  trailerVin: string | null;
  trailerObservations: string | null;
  trailerTare: number | null;
  trailerSealCount: number | null;
  trailerLengthM: string | null;
  trailerWidthM: string | null;
  trailerHeightM: string | null;
  trailerTempSensorCount: number | null;
  trailerIsMapped: boolean | null;
  trailerIsCalibrated: boolean | null;
  // truck specs
  truckVin: string | null;
  truckEngineNumber: string | null;
  truckObservations: string | null;
  truckTare: number | null;
  truckAxles: string | null;
  truckIsMapped: boolean | null;
  truckIsCalibrated: boolean | null;
  // refrig unit extra
  trailerRefrigerationUnitObservations: string | null;
}

interface DriverOption  { id: string; code: string; name: string }
interface UnitOption    { id: string; code: string; name: string; unitTypeCode: string; licensePlate: string | null; brand: string | null; model: string | null; manufactureYear: number | null; unitNumber: number | null; inMobile: boolean }
interface UserOption    { id: string; firstName: string; lastName: string; role: string }
interface ReassignConflict {
  field: "driver" | "truck" | "trailer";
  resourceLabel: string;
  fromMobileId: string;
  fromMobileLabel: string;
  activeTripsCount: number;
}

const EMPTY_FORM = {
  fleet:      "" as Fleet | "",
  workLines:  [] as WorkLine[],
  operatorId: "",
  driverId:   "",
  truckId:    "",
  trailerId:  "",
  notes:      "",
};
type MobileFormState = typeof EMPTY_FORM;

const PAGE_SIZE = 25;

const TRUCK_TYPE_CODES   = ["C", "TRACTORGNC"];
const TRAILER_TYPE_CODES = ["S", "BATEA", "BIVUELCO", "PLAYO"];

// ─── Component ────────────────────────────────────────────────────────────────

export default function MobilesPage() {
  const { can } = usePermissions();
  const qc = useQueryClient();

  const [search, setSearch]                   = useState("");
  const [page, setPage]                       = useState(1);
  const [sheetOpen, setSheetOpen]             = useState(false);
  const [deleteOpen, setDeleteOpen]           = useState(false);
  const [editing, setEditing]                 = useState<MobileRow | null>(null);
  const [deleting, setDeleting]               = useState<MobileRow | null>(null);
  const [form, setForm]                       = useState<MobileFormState>({ ...EMPTY_FORM });
  const [formError, setFormError]             = useState<string | null>(null);
  const [filterWorkLines, setFilterWorkLines] = useState<WorkLine[]>(["nacional", "internacional", "batea"]);
  const [filterDriver, setFilterDriver]       = usePersistedState<string>("mobiles:filterDriver", "");
  const [filterOperatorIds, setFilterOperatorIds] = useState<string[]>([]);
  const [filterFleets, setFilterFleets]       = useState<Fleet[]>([]);
  const [filterStatus, setFilterStatus]       = useState<'activo' | 'alistado' | 'incompleto' | null>(null);
  const [filterFuelType, setFilterFuelType]   = useState<'diesel' | 'gnc' | null>(null);
  const [filterVisibility, setFilterVisibility] = useState<'active' | 'deleted' | 'all'>('active');
  const [detailType, setDetailType]           = useState<"driver" | "truck" | "trailer" | "refrig" | null>(null);
  const [detailRow, setDetailRow]             = useState<MobileRow | null>(null);
  const [shareRow, setShareRow]               = useState<MobileRow | null>(null);
  const [copied, setCopied]                   = useState(false);
  const [restoringId, setRestoringId]         = useState<string | null>(null);
  const [reassignPreview, setReassignPreview]  = useState<ReassignConflict[] | null>(null);
  const [reassignPending, setReassignPending]  = useState<MobileFormState | null>(null);
  const [truckSearch, setTruckSearch]           = useState("");
  const [trailerSearch, setTrailerSearch]       = useState("");
  const [truckSearchDebounced, setTruckSearchDebounced] = useState("");
  const [trailerSearchDebounced, setTrailerSearchDebounced] = useState("");

  useEffect(() => { setPage(1); }, [search, filterDriver, filterWorkLines, filterOperatorIds, filterFleets, filterStatus, filterFuelType, filterVisibility]);

  useEffect(() => {
    const timeout = setTimeout(() => setTruckSearchDebounced(truckSearch), 300);
    return () => clearTimeout(timeout);
  }, [truckSearch]);

  useEffect(() => {
    const timeout = setTimeout(() => setTrailerSearchDebounced(trailerSearch), 300);
    return () => clearTimeout(timeout);
  }, [trailerSearch]);

  const {
    drivers: driverFilterOptions,
    onSearchChange: onDriverSearchChange,
    inputValue: driverInputValue,
    isMinLength: driverIsMinLength,
  } = useDriversSearch();

  // ── Queries ──────────────────────────────────────────────────────────────────

  const { data: mobilesData = [], isLoading } = useQuery({
    queryKey: ["mobiles", filterVisibility],
    queryFn:  async () => {
      const query = filterVisibility === 'deleted' ? { deleted: "true" } : filterVisibility === 'all' ? { deleted: "all" } : {};
      const res = await api["mobiles"].$get({ query });
      if (!res.ok) throw new Error("Error al cargar móviles");
      return res.json() as unknown as MobileRow[];
    },
  });

  const { data: driverOptions = [] } = useQuery({
    queryKey: ["drivers-select"],
    queryFn:  async () => {
      const res = await api.providers.drivers.$get({ query: { deleted: "false", pageSize: "500" } } as any);
      if (!res.ok) return [];
      const json = await res.json() as { data: DriverOption[] };
      return json.data;
    },
    staleTime: 60_000,
  });

  const { data: truckUnitOptions = [] } = useQuery({
    queryKey: ["units-select", "truck", truckSearchDebounced],
    queryFn:  async () => {
      const query: Record<string, string> = {
        deleted: "false",
        pageSize: "500",
        unitTypeCodes: TRUCK_TYPE_CODES.join(","),
      };
      if (truckSearchDebounced.trim()) query.search = truckSearchDebounced.trim();
      const res = await api["units"].$get({ query });
      if (!res.ok) return [];
      const body = await res.json() as { data: UnitOption[]; total: number };
      return body.data ?? [];
    },
    staleTime: 60_000,
  });

  const { data: trailerUnitOptions = [] } = useQuery({
    queryKey: ["units-select", "trailer", trailerSearchDebounced],
    queryFn:  async () => {
      const query: Record<string, string> = {
        deleted: "false",
        pageSize: "500",
        unitTypeCodes: TRAILER_TYPE_CODES.join(","),
      };
      if (trailerSearchDebounced.trim()) query.search = trailerSearchDebounced.trim();
      const res = await api["units"].$get({ query });
      if (!res.ok) return [];
      const body = await res.json() as { data: UnitOption[]; total: number };
      return body.data ?? [];
    },
    staleTime: 60_000,
  });

  const { data: userOptions = [] } = useQuery({
    queryKey: ["users-operators"],
    queryFn:  async () => {
      const res = await api["users"].$get({ query: {} });
      if (!res.ok) return [];
      const all = await res.json() as UserOption[];
      return all.filter(u => u.role === "operator" || u.role === "fleet_manager");
    },
    staleTime: 5 * 60_000,
  });

  const truckOptions = truckUnitOptions;
  const trailerOptions = trailerUnitOptions;

  // Whether the currently assigned resource is soft-deleted (bad state).
  // Detected from the editing row directly — not from the options list (which
  // loads async and may be empty when the sheet opens).
  const truckIsDeleted   = !!(editing?.truckId   && editing.truckDeletedAt);
  const trailerIsDeleted = !!(editing?.trailerId  && editing.trailerDeletedAt);
  const driverIsDeleted  = !!(editing?.driverId   && editing.driverDeletedAt);

  // Fallback options for the currently assigned active resource when unitOptions
  // hasn't loaded yet (async race on sheet open). Injected into the select so
  // the current value is always visible, even before the full list arrives.
  // Only injected when the resource is active (not deleted) and not yet in the list.
  const truckFallbackOption = useMemo(() => {
    if (!editing?.truckId || editing.truckDeletedAt) return null;
    if (truckOptions.some(u => u.id === editing.truckId)) return null;
    return {
      value: editing.truckId,
      label: editing.truckLicensePlate ?? editing.truckCode ?? editing.truckId,
      sublabel: [editing.truckUnitNumber != null ? `M${editing.truckUnitNumber}` : null, editing.truckBrand, editing.truckModel]
        .filter(Boolean).join(" · ") || undefined,
    };
  }, [editing, truckOptions]);

  const trailerFallbackOption = useMemo(() => {
    if (!editing?.trailerId || editing.trailerDeletedAt) return null;
    if (trailerOptions.some(u => u.id === editing.trailerId)) return null;
    return {
      value: editing.trailerId,
      label: editing.trailerLicensePlate ?? editing.trailerCode ?? editing.trailerId,
      sublabel: editing.trailerBrand ?? undefined,
    };
  }, [editing, trailerOptions]);

  const driverFallbackOption = useMemo(() => {
    if (!editing?.driverId || editing.driverDeletedAt) return null;
    if (driverOptions.some(d => d.id === editing.driverId)) return null;
    return {
      value: editing.driverId,
      label: editing.driverName ?? editing.driverId,
      sublabel: editing.driverCode ?? undefined,
    };
  }, [editing, driverOptions]);

  const uniqueOperators = useMemo(() => {
    const ops = new Map<string, string>();
    mobilesData.forEach(m => {
      if (m.operatorId && m.operatorName && !ops.has(m.operatorId)) {
        ops.set(m.operatorId, m.operatorName);
      }
    });
    return Array.from(ops.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [mobilesData]);

  // ── Filtering ────────────────────────────────────────────────────────────────

  const filtered = useMemo(() => {
    const term = search.toLowerCase().trim();
    return mobilesData.filter((m) => {
      if (term && !(
        (m.driverName ?? "").toLowerCase().includes(term) ||
        (m.operatorName ?? "").toLowerCase().includes(term) ||
        (m.truckLicensePlate ?? "").toLowerCase().includes(term) ||
        (m.trailerLicensePlate ?? "").toLowerCase().includes(term) ||
        (m.driverCode ?? "").toLowerCase().includes(term) ||
        (m.truckCode ?? "").toLowerCase().includes(term) ||
        (m.trailerCode ?? "").toLowerCase().includes(term) ||
        (m.truckUnitNumber != null && String(m.truckUnitNumber).includes(term))
      )) return false;

      // Filter by driver
      if (filterDriver && m.driverName !== filterDriver) return false;

      // Filter by work lines
      if (filterWorkLines.length < 3) {
        const hasMatchingWorkLine = m.workLines.some(wl => filterWorkLines.includes(wl));
        if (!hasMatchingWorkLine) return false;
      }

      // Filter by operators
      if (filterOperatorIds.length > 0 && !filterOperatorIds.includes(m.operatorId ?? "")) return false;

      // Filter by fleet
      if (filterFleets.length > 0 && !filterFleets.includes(m.fleet as Fleet)) return false;

      // Filter by status
      if (filterStatus !== null && mobileStatus(m) !== filterStatus) return false;

      // Filter by fuel type
      if (filterFuelType !== null && m.truckFuelType !== filterFuelType) return false;

      return true;
    });
  }, [mobilesData, search, filterDriver, filterWorkLines, filterOperatorIds, filterFleets, filterStatus, filterFuelType]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged      = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // ── Mutations ────────────────────────────────────────────────────────────────

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["mobiles"] }); // invalidates both active + deleted
    qc.invalidateQueries({ queryKey: ["units-select"] });
    qc.invalidateQueries({ queryKey: ["drivers-select"] });
  };

  const buildPayload = (f: MobileFormState) => ({
    fleet:      (f.fleet || null) as Fleet | null,
    workLines:  f.workLines,
    operatorId: f.operatorId || null,
    driverId:   f.driverId   || null,
    truckId:    f.truckId    || null,
    trailerId:  f.trailerId  || null,
    notes:      f.notes      || null,
  });

  const createMut = useMutation({
    mutationFn: async (f: MobileFormState) => {
      const res = await api["mobiles"].$post({ json: buildPayload(f) });
      if (!res.ok) { const b = await res.json() as { error?: string }; throw new Error(b.error ?? "Error al crear"); }
      return res.json();
    },
    onSuccess: () => { invalidate(); toast.success("Móvil creado"); closeSheet(); },
    onError: (err: Error) => setFormError(err.message),
  });

  const updateMut = useMutation({
    mutationFn: async (f: MobileFormState) => {
      if (!editing) throw new Error("Sin móvil seleccionado");
      const res = await api["mobiles"][":id"].$patch({ param: { id: editing.id }, json: buildPayload(f) });
      if (!res.ok) { const b = await res.json() as { error?: string }; throw new Error(b.error ?? "Error al actualizar"); }
      return res.json();
    },
    onSuccess: () => { invalidate(); toast.success("Móvil actualizado"); closeSheet(); },
    onError: (err: Error) => setFormError(err.message),
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      const res = await api["mobiles"][":id"].$delete({ param: { id } });
      if (!res.ok) { const b = await res.json() as { error?: string }; throw new Error(b.error ?? "Error al eliminar"); }
    },
    onSuccess: () => { invalidate(); toast.success("Móvil eliminado"); setDeleteOpen(false); setDeleting(null); },
    onError: (err: Error) => setFormError(err.message),
  });

  const restoreMut = useMutation({
    mutationFn: (id: string) =>
      authorizedFetch(`${API_URL}/api/mobiles/${id}/restore`, { method: "PATCH" }),
    onSuccess: () => { invalidate(); toast.success("Móvil restaurado"); setRestoringId(null); },
    onError: (err: Error) => { toast.error(err.message); setRestoringId(null); },
  });



  // ── Helpers ────────────────────────────────────────────────────────────────

  function closeSheet() {
    setSheetOpen(false);
    setEditing(null);
    setForm({ ...EMPTY_FORM });
    setFormError(null);
    setReassignPreview(null);
    setReassignPending(null);
    setTruckSearch("");
    setTrailerSearch("");
    setTruckSearchDebounced("");
    setTrailerSearchDebounced("");
  }

  function closeDetail() {
    setDetailType(null);
    setDetailRow(null);
  }

  function buildShareText(m: MobileRow): string {
    const SEP = "────────────────────";
    const num = m.truckUnitNumber != null ? `M${m.truckUnitNumber}` : "Móvil";
    const fleet = m.fleet ? ` · ${FLEET_LABELS[m.fleet]}` : "";
    const wls = m.workLines.map(w => WORK_LINE_LABELS[w]).join(" / ");
    const lines: string[] = [
      `*${num}${fleet} | ${wls}*`,
      SEP,
    ];

    if (m.driverName) {
      lines.push("*CHOFER*");
      lines.push(m.driverName);
      if (m.driverDni)           lines.push(`DNI: ${m.driverDni}`);
      if (m.driverPhoneCdc)      lines.push(`Tel CDC: ${m.driverPhoneCdc}`);
      if (m.driverPhonePersonal) lines.push(`Tel Personal: ${m.driverPhonePersonal}`);
      lines.push("");
    }

    if (m.truckLicensePlate || m.truckBrand) {
      lines.push(SEP);
      lines.push("*CAMION*");
      const brand = [m.truckBrand, m.truckModel].filter(Boolean).join(" ");
      if (brand)                    lines.push(brand);
      if (m.truckManufactureYear)   lines.push(`Ano: ${m.truckManufactureYear}`);
      if (m.truckTare != null)      lines.push(`Tara: ${m.truckTare} kg`);
      if (m.truckLicensePlate)      lines.push(`Patente: ${m.truckLicensePlate}`);
      if (m.truckUnitNumber != null) lines.push(`N\u00b0 unidad: ${m.truckUnitNumber}`);
      lines.push("");
    }

    if (m.trailerLicensePlate || m.trailerBrand) {
      lines.push(SEP);
      lines.push("*SEMI*");
      if (m.trailerBrand)              lines.push(m.trailerBrand);
      if (m.trailerManufactureYear)    lines.push(`Ano: ${m.trailerManufactureYear}`);
      if (m.trailerTare != null)       lines.push(`Tara: ${m.trailerTare} kg`);
      if (m.trailerLicensePlate)       lines.push(`Patente: ${m.trailerLicensePlate}`);
      if (m.trailerTypeName)           lines.push(m.trailerTypeName);
    }

    return lines.join("\n").trim();
  }

  function handleCopy(text: string) {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function openCreate() {
    setEditing(null);
    setForm({ ...EMPTY_FORM });
    setFormError(null);
    setSheetOpen(true);
  }

  function openEdit(m: MobileRow) {
    setEditing(m);
    setForm({
      fleet:      m.fleet ?? "",
      workLines:  m.workLines,
      operatorId: m.operatorId ?? "",
      // If the resource is soft-deleted, clear it from the form so the
      // operator is forced to pick an active one. The warning banner explains why.
      driverId:   m.driverDeletedAt  ? "" : (m.driverId  ?? ""),
      truckId:    m.truckDeletedAt   ? "" : (m.truckId   ?? ""),
      trailerId:  m.trailerDeletedAt ? "" : (m.trailerId ?? ""),
      notes:      m.notes      ?? "",
    });
    setFormError(null);
    setSheetOpen(true);
  }

  function toggleWorkLine(wl: WorkLine) {
    setForm(f => ({
      ...f,
      workLines: f.workLines.includes(wl)
        ? f.workLines.filter(x => x !== wl)
        : [...f.workLines, wl],
    }));
  }

  // ── Export ───────────────────────────────────────────────────────────────────

  function csvEsc(v: string | number | null | undefined): string {
    if (v == null) return "";
    const s = String(v);
    if (s.includes(",") || s.includes('"') || s.includes("\n")) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  }

  function handleExport() {
    const headers = [
      "N°", "Chofer", "DNI Chofer",
      "Patente Camión", "Nombre Camión", "Año Camión",
      "Patente Semi", "Marca Semi", "Año Semi",
      "Código Equipo Frío", "Nombre Equipo Frío",
      "Línea", "Flota", "Estado",
    ];

    const dataRows = filtered.map(m => [
      m.truckUnitNumber,
      m.driverName,
      m.driverDni,
      m.truckLicensePlate,
      [m.truckBrand, m.truckModel].filter(Boolean).join(" ") || null,
      m.truckManufactureYear,
      m.trailerLicensePlate,
      m.trailerBrand,
      m.trailerManufactureYear,
      m.trailerRefrigerationUnitCode,
      m.trailerRefrigerationUnitName,
      m.workLines.map(wl => WORK_LINE_LABELS[wl]).join(" / "),
      m.fleet ? FLEET_LABELS[m.fleet] : null,
      mobileStatus(m),
    ]);

    const csv = [
      headers.join(","),
      ...dataRows.map(row => row.map(csvEsc).join(",")),
    ].join("\r\n");

    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `moviles-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);
    if (form.workLines.length === 0) { setFormError("Debe seleccionar al menos una línea de trabajo"); return; }

    // Only check for conflicts when editing (not creating)
    if (editing) {
      // Detect which resources changed and might be "stolen" from another mobile
      const payload: { driverId?: string | null; truckId?: string | null; trailerId?: string | null } = {};
      if (form.driverId  !== (editing.driverId  ?? "")) payload.driverId  = form.driverId  || null;
      if (form.truckId   !== (editing.truckId   ?? "")) payload.truckId   = form.truckId   || null;
      if (form.trailerId !== (editing.trailerId ?? "")) payload.trailerId = form.trailerId || null;

      const hasChangedResources = payload.driverId !== undefined || payload.truckId !== undefined || payload.trailerId !== undefined;

      if (hasChangedResources) {
        // Call preview endpoint to detect conflicts
        try {
          const res = await authorizedFetch(`${API_URL}/api/mobiles/${editing.id}/reassign-preview`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          });
          const data = await res.json() as { conflicts: ReassignConflict[] };
          if (data.conflicts.length > 0) {
            // Show confirmation dialog
            setReassignPreview(data.conflicts);
            setReassignPending(form);
            return; // Don't save yet
          }
        } catch {
          // If preview fails, proceed with save anyway (API will handle it)
        }
      }
    }

    if (editing) updateMut.mutate(form);
    else createMut.mutate(form);
  }

  function confirmReassign() {
    if (!reassignPending) return;
    updateMut.mutate(reassignPending, {
      onSuccess: () => {
        setReassignPreview(null);
        setReassignPending(null);
      },
      onError: () => {
        setReassignPreview(null);
        setReassignPending(null);
      },
    });
  }

  function cancelReassign() {
    setReassignPreview(null);
    setReassignPending(null);
  }

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <>
      <SiteHeader title="Asignaciones" />
      <div className="flex flex-1 flex-col px-4 py-6 lg:px-6">
        <div className="space-y-4 w-full">

          {/* Toolbar: Search and New Button */}
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="relative flex-1 min-w-[220px]">
              <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por chofer, patente, operador o N°..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8"
              />
            </div>

            <Select
              value={filterVisibility}
              onValueChange={(v: 'active' | 'deleted' | 'all' | null) => {
                if (!v) return;
                setFilterVisibility(v);
                if (v !== 'active') setFilterStatus(null);
              }}
            >
              <SelectTrigger className="h-9 text-sm w-32">
                <SelectValue>
                  {filterVisibility === 'active' ? 'Activos' : filterVisibility === 'deleted' ? 'Eliminados' : 'Todos'}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Activos</SelectItem>
                <SelectItem value="deleted">Eliminados</SelectItem>
                <SelectItem value="all">Todos</SelectItem>
              </SelectContent>
            </Select>

            <Button
              variant="outline"
              size="sm"
              className="gap-2"
              onClick={handleExport}
              disabled={filtered.length === 0}
              title={`Exportar ${filtered.length} asignaciones visibles`}
            >
              <DownloadIcon className="size-4" /> Exportar CSV
            </Button>

            {can("write", "mobiles") && (
              <Button onClick={openCreate} className="gap-2" size="sm">
                <PlusIcon className="size-4" /> Nueva asignación
              </Button>
            )}
          </div>

          {/* Filters Row: Driver, work lines and operators */}
          <div className="flex flex-wrap items-center gap-4 mb-4">
            {/* Driver filter */}
            <div className="w-44">
              <SearchableSelect
                compact
                emptyValue=""
                options={[
                  // Keep selected driver visible in trigger even before the user types
                  ...(filterDriver && !driverFilterOptions.find(d => d.name === filterDriver)
                    ? [{ value: filterDriver, label: filterDriver }]
                    : []),
                  ...driverFilterOptions.map(d => ({ value: d.name, label: d.name })),
                ]}
                value={filterDriver}
                onValueChange={(v) => { setFilterDriver(v); setPage(1) }}
                onSearchChange={onDriverSearchChange}
                placeholder="Todos los choferes"
                searchPlaceholder={driverIsMinLength ? "Buscando..." : "Escribí 3 letras para buscar"}
                emptyLabel={
                  driverInputValue.trim().length > 0 && !driverIsMinLength
                    ? "Escribí al menos 3 letras"
                    : "Sin resultados"
                }
              />
            </div>

            {/* Work Lines Filter */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-muted-foreground">Líneas:</span>
              <div className="flex gap-1.5">
                {WORK_LINES.map(wl => (
                  <button
                    key={wl}
                    onClick={() => setFilterWorkLines(prev =>
                      prev.includes(wl)
                        ? prev.filter(x => x !== wl)
                        : [...prev, wl]
                    )}
                    className={`text-xs px-2 py-1 rounded font-medium transition-colors ${
                      filterWorkLines.includes(wl)
                        ? WORK_LINE_COLORS[wl]
                        : "bg-muted text-muted-foreground hover:bg-muted/80"
                    }`}
                  >
                    {WORK_LINE_LABELS[wl]}
                  </button>
                ))}
              </div>
            </div>

            {/* Operators Filter */}
            {uniqueOperators.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-muted-foreground">Operadores:</span>
                <div className="flex gap-1.5 flex-wrap">
                  {uniqueOperators.map(([id, name]) => (
                    <button
                      key={id}
                      onClick={() => setFilterOperatorIds(prev =>
                        prev.includes(id)
                          ? prev.filter(x => x !== id)
                          : [...prev, id]
                      )}
                      className={`text-xs px-2 py-1 rounded font-medium transition-colors ${
                        filterOperatorIds.includes(id)
                          ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                          : "bg-muted text-muted-foreground hover:bg-muted/80"
                      }`}
                    >
                      {name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Fleet Filter */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-muted-foreground">Flota:</span>
              <div className="flex gap-1.5">
                {FLEETS.map(f => (
                  <button
                    key={f}
                    onClick={() => setFilterFleets(prev =>
                      prev.includes(f)
                        ? prev.filter(x => x !== f)
                        : [...prev, f]
                    )}
                    className={`text-xs px-2 py-1 rounded font-medium transition-colors ${
                      filterFleets.includes(f)
                        ? FLEET_COLORS[f]
                        : "bg-muted text-muted-foreground hover:bg-muted/80"
                    }`}
                  >
                    {FLEET_LABELS[f]}
                  </button>
                ))}
              </div>
            </div>

            {/* Fuel Type Filter */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-muted-foreground">Combustible:</span>
              <div className="flex gap-1.5">
                <button
                  onClick={() => setFilterFuelType(prev => prev === 'diesel' ? null : 'diesel')}
                  className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded font-medium transition-colors ${
                    filterFuelType === 'diesel'
                      ? "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400"
                      : "bg-muted text-muted-foreground hover:bg-muted/80"
                  }`}
                >
                  <FlameIcon className="size-3" /> Diesel
                </button>
                <button
                  onClick={() => setFilterFuelType(prev => prev === 'gnc' ? null : 'gnc')}
                  className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded font-medium transition-colors ${
                    filterFuelType === 'gnc'
                      ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                      : "bg-muted text-muted-foreground hover:bg-muted/80"
                  }`}
                >
                  <LeafIcon className="size-3" /> GNC
                </button>
              </div>
            </div>

            {/* Status Filter — hidden when showing deleted */}
            {filterVisibility === 'active' && <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-muted-foreground">Estado:</span>
              <div className="flex gap-1.5">
                <button
                  onClick={() => setFilterStatus(prev => prev === 'activo' ? null : 'activo')}
                  className={`text-xs px-2 py-1 rounded font-medium transition-colors ${
                    filterStatus === 'activo'
                      ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                      : "bg-muted text-muted-foreground hover:bg-muted/80"
                  }`}
                >
                  Activos
                </button>
                <button
                  onClick={() => setFilterStatus(prev => prev === 'alistado' ? null : 'alistado')}
                  className={`text-xs px-2 py-1 rounded font-medium transition-colors ${
                    filterStatus === 'alistado'
                      ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                      : "bg-muted text-muted-foreground hover:bg-muted/80"
                  }`}
                >
                  Alistados
                </button>
                <button
                  onClick={() => setFilterStatus(prev => prev === 'incompleto' ? null : 'incompleto')}
                  className={`text-xs px-2 py-1 rounded font-medium transition-colors ${
                    filterStatus === 'incompleto'
                      ? "bg-muted text-foreground"
                      : "bg-muted text-muted-foreground hover:bg-muted/80"
                  }`}
                >
                  Incompletos
                </button>
              </div>
            </div>}
          </div>

          {/* Table */}
          <div className="rounded-lg border overflow-x-auto">
            <Table className="w-full">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10 text-center">N°</TableHead>
                  <TableHead className="min-w-36">Chofer</TableHead>
                  <TableHead className="w-12 text-center">Resp.</TableHead>
                  <TableHead className="min-w-44">Camión</TableHead>
                  <TableHead className="min-w-36">Semi</TableHead>
                  <TableHead className="w-28">Líneas</TableHead>
                  <TableHead className="w-20 text-center">Flota</TableHead>
                  <TableHead className="w-24 text-center">Estado</TableHead>
                  <TableHead className="w-20 text-right pr-4">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-16 text-muted-foreground">
                      Cargando...
                    </TableCell>
                  </TableRow>
                ) : paged.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={can("write", "mobiles") ? 8 : 7} className="text-center py-16 text-muted-foreground">
                        {filterVisibility === 'deleted' ? "No hay asignaciones eliminadas" : search || filterDriver || filterWorkLines.length < 3 || filterOperatorIds.length > 0 ? "Sin resultados" : "No hay asignaciones"}
                    </TableCell>
                  </TableRow>
                ) : paged.map((m) => (
                  <TableRow key={m.id} className={m.deletedAt ? "opacity-50" : undefined}>

                    {/* N° Unidad */}
                    <TableCell className="text-center">
                      {m.truckUnitNumber != null
                        ? <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded font-medium">{m.truckUnitNumber}</span>
                        : <span className="text-muted-foreground text-xs">—</span>}
                    </TableCell>

                    {/* Chofer */}
                    <TableCell>
                      {m.driverId ? (
                        <div className="inline-flex items-center gap-1.5">
                          {!m.driverDeletedAt && m.driverStatus === "active"
                            ? <span className="size-1.5 rounded-full shrink-0 bg-emerald-500" />
                            : <button className="size-1.5 rounded-full shrink-0 bg-red-500 hover:bg-red-400 cursor-help focus:outline-none" title={inactiveDotReason("Chofer", m.driverDeletedAt)} onClick={(e) => { e.stopPropagation(); toast.info(inactiveDotReason("Chofer", m.driverDeletedAt)); }} />
                          }
                          <button
                            onClick={() => { setDetailType("driver"); setDetailRow(m); }}
                            className="inline-flex items-center gap-1 text-sm font-medium leading-tight text-primary hover:underline"
                          >
                            {m.driverName ?? "—"}
                            <ExternalLinkIcon className="size-3 shrink-0 opacity-60" />
                          </button>
                        </div>
                      ) : (
                        <div className="text-sm font-medium leading-tight text-muted-foreground">—</div>
                      )}
                    </TableCell>

                    {/* Responsable */}
                    <TableCell className="text-center">
                      {m.operatorName ? (
                        <Avatar size="sm" className="mx-auto" title={m.operatorName}>
                          <AvatarFallback
                            className="text-white text-[10px] font-semibold"
                            style={{ backgroundColor: avatarColor(m.operatorName) }}
                          >
                            {avatarInitials(m.operatorName)}
                          </AvatarFallback>
                        </Avatar>
                      ) : (
                        <span className="text-muted-foreground/40 text-xs">—</span>
                      )}
                    </TableCell>

                    {/* Camín */}
                    <TableCell>
                      {m.truckId ? (
                        <div className="space-y-1">
                          <div className="inline-flex items-center gap-1.5">
                            {!m.truckDeletedAt && m.truckStatus === "active"
                              ? <span className="size-1.5 rounded-full shrink-0 bg-emerald-500" />
                              : <button className="size-1.5 rounded-full shrink-0 bg-red-500 hover:bg-red-400 cursor-help focus:outline-none" title={inactiveDotReason("Camión", m.truckDeletedAt)} onClick={(e) => { e.stopPropagation(); toast.info(inactiveDotReason("Camión", m.truckDeletedAt)); }} />
                            }
                            <button
                              onClick={() => { setDetailType("truck"); setDetailRow(m); }}
                              className="inline-flex items-center gap-1 text-sm font-medium leading-tight text-primary hover:underline"
                            >
                              {[m.truckBrand, m.truckModel].filter(Boolean).join(" ") || `Camión #${m.truckUnitNumber}` || "—"}
                              <ExternalLinkIcon className="size-3 shrink-0 opacity-60" />
                            </button>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            {m.truckLicensePlate && (
                              <span className="font-mono">{m.truckLicensePlate}</span>
                            )}
                            {m.truckManufactureYear && (
                              <span>{m.truckManufactureYear}</span>
                            )}
                            {m.truckFuelType === 'gnc' && (
                              <span title="GNC"><LeafIcon className="size-3 text-emerald-500" /></span>
                            )}
                            {m.truckFuelType === 'diesel' && (
                              <span title="Diesel"><FlameIcon className="size-3 text-red-400" /></span>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="text-sm font-medium leading-tight text-muted-foreground">—</div>
                      )}
                    </TableCell>

                    {/* Semi */}
                    <TableCell>
                      {m.trailerId ? (
                        <div className="space-y-1">
                          <div className="inline-flex items-center gap-1.5">
                            {!m.trailerDeletedAt && m.trailerStatus === "active"
                              ? <span className="size-1.5 rounded-full shrink-0 bg-emerald-500" />
                              : <button className="size-1.5 rounded-full shrink-0 bg-red-500 hover:bg-red-400 cursor-help focus:outline-none" title={inactiveDotReason("Semi", m.trailerDeletedAt)} onClick={(e) => { e.stopPropagation(); toast.info(inactiveDotReason("Semi", m.trailerDeletedAt)); }} />
                            }
                            <button
                              onClick={() => { setDetailType("trailer"); setDetailRow(m); }}
                              className="inline-flex items-center gap-1 text-sm font-medium leading-tight text-primary hover:underline"
                            >
                              {m.trailerBrand ?? m.trailerLicensePlate ?? "Semi"}
                              <ExternalLinkIcon className="size-3 shrink-0 opacity-60" />
                            </button>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            {m.trailerLicensePlate && (
                              <span className="font-mono">{m.trailerLicensePlate}</span>
                            )}
                            {m.trailerTypeName && (
                              <span>{m.trailerTypeName}</span>
                            )}
                            {m.trailerManufactureYear && (
                              <span>{m.trailerManufactureYear}</span>
                            )}
                          </div>
                          {m.trailerRefrigerationUnitCode && (
                            <div className="inline-flex items-center gap-1">
                              <span
                                className={`size-1.5 rounded-full shrink-0 ${m.trailerRefrigerationUnitStatus === 'active' ? 'bg-emerald-500' : 'bg-red-500'}`}
                                title={m.trailerRefrigerationUnitStatus ?? ''}
                              />
                              <button
                                onClick={() => { setDetailType("refrig"); setDetailRow(m); }}
                                className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400 hover:bg-cyan-200 dark:hover:bg-cyan-900/50 transition-colors"
                              >
                                ❄ {m.trailerRefrigerationUnitCode}
                              </button>
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="font-mono text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>

                    {/* Líneas */}
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {m.workLines.map(wl => (
                          <span key={wl} className={`text-[10px] px-1.5 py-0 rounded font-medium ${WORK_LINE_COLORS[wl]}`}>
                            {WORK_LINE_LABELS[wl]}
                          </span>
                        ))}
                      </div>
                    </TableCell>

                    {/* Flota */}
                    <TableCell className="text-center">
                      {m.fleet ? (
                        <span className={`text-[10px] px-1.5 py-0 rounded font-medium ${FLEET_COLORS[m.fleet]}`}>
                          {FLEET_LABELS[m.fleet]}
                        </span>
                      ) : <span className="text-muted-foreground/40 text-xs">—</span>}
                    </TableCell>

                    {/* Estado (calculado) */}
                    <TableCell className="text-center">
                      {(() => {
                        const st = mobileStatus(m);
                        if (st === 'activo') return (
                          <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">Activo</span>
                        );
                        if (st === 'alistado') return (
                          <button
                            className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-900/50 cursor-help focus:outline-none"
                            title="Camión listo, sin chofer asignado"
                            onClick={() => toast.info("Alistado", { description: "Camión activo sin chofer asignado" })}
                          >
                            Alistado
                          </button>
                        );
                        return (
                          <button
                            className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-muted text-muted-foreground hover:bg-muted/70 cursor-help focus:outline-none"
                            title={mobileInactiveReasons(m)}
                            onClick={() => toast.info("Móvil incompleto", { description: mobileInactiveReasons(m) })}
                          >
                            Incompleto
                          </button>
                        );
                      })()}
                    </TableCell>

                    {/* Acciones */}
                    <TableCell className="text-right pr-4">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost" size="icon-sm"
                          onClick={() => { setShareRow(m); setCopied(false); }}
                          title="Compartir por WhatsApp"
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        >
                          <Share2Icon className="size-3.5" />
                        </Button>
                        {can("write", "mobiles") && (
                          m.deletedAt ? (
                            <Button
                              variant="ghost" size="icon-sm"
                              onClick={() => { setRestoringId(m.id); restoreMut.mutate(m.id); }}
                              disabled={restoringId === m.id}
                              className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                              title="Restaurar móvil"
                            >
                              <RestoreIcon className="size-3.5" />
                            </Button>
                          ) : (
                            <>
                              <Button variant="ghost" size="icon-sm" onClick={() => openEdit(m)} title="Editar">
                                <PencilIcon className="size-3.5" />
                              </Button>
                              <Button
                                variant="ghost" size="icon-sm"
                                onClick={() => { setDeleting(m); setDeleteOpen(true); }}
                                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                title="Eliminar"
                              >
                                <Trash2Icon className="size-3.5" />
                              </Button>
                            </>
                          )
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              totalItems={filtered.length}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
            />
          </div>
        </div>
      </div>

      {/* ── Edit / Create Sheet ─────────────────────────────────────────────── */}
      <Sheet open={sheetOpen} onOpenChange={(o) => { if (!o) closeSheet(); }}>
        <SheetContent side="right" className="sm:max-w-sm overflow-y-auto flex flex-col">
          <SheetHeader>
            <SheetTitle>{editing ? "Editar asignación" : "Nueva asignación"}</SheetTitle>
            <SheetDescription>Asignación de chofer, camión y semi-trailer.</SheetDescription>
          </SheetHeader>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4 px-4 pb-4 flex-1">

            {/* Flota */}
            <div className="grid gap-1.5">
              <Label>Flota</Label>
              <Select
                value={form.fleet || "__none__"}
                onValueChange={(v) => setForm(f => ({ ...f, fleet: v === "__none__" ? "" : v as Fleet }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sin asignar">
                    {form.fleet ? FLEET_LABELS[form.fleet as Fleet] : "Sin asignar"}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Sin asignar</SelectItem>
                  {FLEETS.map(f => (
                    <SelectItem key={f} value={f}>{FLEET_LABELS[f]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Líneas de trabajo */}
            <div className="grid gap-2">
              <Label>Líneas de trabajo <span className="text-destructive">*</span></Label>
              <div className="flex flex-wrap gap-2">
                {WORK_LINES.map(wl => (
                  <button
                    key={wl}
                    type="button"
                    onClick={() => toggleWorkLine(wl)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                      form.workLines.includes(wl)
                        ? `${WORK_LINE_COLORS[wl]} border-current font-medium`
                        : "border-border text-muted-foreground hover:border-foreground"
                    }`}
                  >
                    {WORK_LINE_LABELS[wl]}
                  </button>
                ))}
              </div>
            </div>

            {/* Operador */}
            <div className="grid gap-1.5">
              <Label>Operador responsable</Label>
              <SearchableSelect
                placeholder="Sin asignar"
                searchPlaceholder="Buscar operador..."
                value={form.operatorId}
                onValueChange={(v) => setForm(f => ({ ...f, operatorId: v }))}
                options={[
                  { value: "", label: "Sin asignar" },
                  ...userOptions.map(u => ({ value: u.id, label: `${u.firstName} ${u.lastName}` }))
                ]}
              />
            </div>

            {/* Chofer */}
            <div className="grid gap-1.5">
              <Label>Chofer</Label>
              {driverIsDeleted && (
                <p className="text-xs text-destructive bg-destructive/10 rounded px-2 py-1">
                  ⚠️ El chofer asignado ({editing?.driverName ?? "desconocido"}) fue eliminado. Seleccioná uno activo.
                </p>
              )}
              <SearchableSelect
                placeholder="Sin asignar"
                searchPlaceholder="Buscar chofer..."
                value={form.driverId}
                onValueChange={(v) => setForm(f => ({ ...f, driverId: v }))}
                options={[
                  { value: "", label: "Sin asignar" },
                  ...(driverFallbackOption ? [driverFallbackOption] : []),
                  ...driverOptions.map(d => {
                    const assignedTo = mobilesData.find(m => m.driverId === d.id && (!editing || m.id !== editing.id));
                    const assignedLabel = assignedTo
                      ? `⚠️ En ${[assignedTo.truckLicensePlate, assignedTo.trailerLicensePlate].filter(Boolean).join(" · ") || "otro móvil"}`
                      : d.code;
                    return {
                      value: d.id,
                      label: d.name,
                      sublabel: assignedLabel,
                      disabled: false
                    };
                  })
                ]}
              />
            </div>

            {/* Camión */}
            <div className="grid gap-1.5">
              <Label>Camión (Tractor)</Label>
              {truckIsDeleted && (
                <p className="text-xs text-destructive bg-destructive/10 rounded px-2 py-1">
                  ⚠️ El camión asignado ({editing?.truckLicensePlate ?? "desconocido"}) fue eliminado. Seleccioná uno activo.
                </p>
              )}
              <SearchableSelect
                placeholder="Sin asignar"
                searchPlaceholder="Buscar por patente, código, marca o N°..."
                value={form.truckId}
                onValueChange={(v) => setForm(f => ({ ...f, truckId: v }))}
                onSearchChange={setTruckSearch}
                options={[
                  { value: "", label: "Sin asignar" },
                  ...(truckFallbackOption ? [truckFallbackOption] : []),
                  ...truckOptions.map(u => {
                    const assignedTo = mobilesData.find(m => m.truckId === u.id && (!editing || m.id !== editing.id));
                    const normalSublabel = [u.unitNumber != null ? `M${u.unitNumber}` : null, u.brand, u.model]
                      .filter(Boolean).join(" · ") || undefined;
                    const assignedLabel = assignedTo
                      ? `⚠️ En ${[assignedTo.driverName, assignedTo.trailerLicensePlate].filter(Boolean).join(" · ") || "otro móvil"}`
                      : normalSublabel;
                    return {
                      value: u.id,
                      label: u.licensePlate ?? u.code,
                      sublabel: assignedLabel,
                      keywords: [u.code, u.licensePlate, u.brand, u.model, u.unitNumber != null ? String(u.unitNumber) : null]
                        .filter(Boolean).join(" "),
                      disabled: false
                    };
                  })
                ]}
              />
            </div>

            {/* Trailer */}
            <div className="grid gap-1.5">
              <Label>Semi-trailer (Furgón)</Label>
              {trailerIsDeleted && (
                <p className="text-xs text-destructive bg-destructive/10 rounded px-2 py-1">
                  ⚠️ El trailer asignado ({editing?.trailerLicensePlate ?? "desconocido"}) fue eliminado. Seleccioná uno activo.
                </p>
              )}
              <SearchableSelect
                placeholder="Sin asignar"
                searchPlaceholder="Buscar por patente, código o marca..."
                value={form.trailerId}
                onValueChange={(v) => setForm(f => ({ ...f, trailerId: v }))}
                onSearchChange={setTrailerSearch}
                options={[
                  { value: "", label: "Sin asignar" },
                  ...(trailerFallbackOption ? [trailerFallbackOption] : []),
                  ...trailerOptions.map(u => {
                    const assignedTo = mobilesData.find(m => m.trailerId === u.id && (!editing || m.id !== editing.id));
                    const assignedLabel = assignedTo
                      ? `⚠️ En ${[assignedTo.driverName, assignedTo.truckLicensePlate].filter(Boolean).join(" · ") || "otro móvil"}`
                      : (u.brand ?? undefined);
                    return {
                      value: u.id,
                      label: u.licensePlate ?? u.code,
                      sublabel: assignedLabel,
                      keywords: [u.code, u.licensePlate, u.brand, u.model, u.unitNumber != null ? String(u.unitNumber) : null]
                        .filter(Boolean).join(" "),
                      disabled: false
                    };
                  })
                ]}
              />
            </div>

            {/* Notas */}
            <div className="grid gap-1.5">
              <Label htmlFor="mob-notes">Observaciones</Label>
              <textarea
                id="mob-notes"
                value={form.notes}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Observaciones opcionales..."
                rows={3}
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>

            {formError && (
              <p className="text-sm text-destructive bg-destructive/10 rounded px-3 py-2">{formError}</p>
            )}

            <SheetFooter className="px-0 mt-auto pt-4">
              <Button type="button" variant="outline" onClick={closeSheet}>Cancelar</Button>
              <Button type="submit" disabled={createMut.isPending || updateMut.isPending}>
                {createMut.isPending || updateMut.isPending ? "Guardando..." : editing ? "Guardar cambios" : "Crear móvil"}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>

      {/* ── Detail Modals ────────────────────────────────────────────────────── */}

      {/* Driver Detail Dialog */}
      <Dialog open={detailType === "driver" && !!detailRow} onOpenChange={(open) => { if (!open) closeDetail(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Información del Chofer</DialogTitle>
          </DialogHeader>
          {detailRow && (
            <div className="grid gap-4">
              {(detailRow.driverDeletedAt || detailRow.driverStatus !== "active") && (
                <div className="flex items-center gap-2.5 rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-3 py-2.5">
                  <span className="size-2 rounded-full bg-red-500 shrink-0" />
                  <p className="text-sm font-medium text-red-700 dark:text-red-400">
                    {detailRow.driverDeletedAt
                      ? "Chofer eliminado del sistema"
                      : `Chofer inactivo · ${DRIVER_STATUS_LABELS[detailRow.driverStatus ?? ""] ?? detailRow.driverStatus}`
                    }
                  </p>
                </div>
              )}
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium mb-1">Nombre</p>
                <p className="text-base font-semibold">{detailRow.driverName || "—"}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium mb-1">Código</p>
                  <p className="font-mono text-sm bg-muted px-2 py-1 rounded">{detailRow.driverCode || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium mb-1">DNI</p>
                  <p className="font-mono text-sm bg-muted px-2 py-1 rounded">{detailRow.driverDni || "—"}</p>
                </div>
              </div>

              <div className="border-t pt-4">
                <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium mb-3">Contacto</p>
                <div className="grid gap-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Tel. CDC</span>
                    {detailRow.driverPhoneCdc ? (
                      <a
                        href={`tel:${detailRow.driverPhoneCdc}`}
                        className="font-mono text-sm text-primary hover:underline"
                      >
                        {detailRow.driverPhoneCdc}
                      </a>
                    ) : (
                      <span className="text-sm text-muted-foreground">—</span>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Tel. Personal</span>
                    {detailRow.driverPhonePersonal ? (
                      <a
                        href={`tel:${detailRow.driverPhonePersonal}`}
                        className="font-mono text-sm text-primary hover:underline"
                      >
                        {detailRow.driverPhonePersonal}
                      </a>
                    ) : (
                      <span className="text-sm text-muted-foreground">—</span>
                    )}
                  </div>
                </div>
              </div>

              {detailRow.notes && (
                <div className="border-t pt-4">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium mb-1">Observaciones</p>
                  <p className="text-sm text-foreground whitespace-pre-wrap">{detailRow.notes}</p>
                </div>
              )}


            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Truck Detail Dialog */}
      <Dialog open={detailType === "truck" && !!detailRow} onOpenChange={(open) => { if (!open) closeDetail(); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Información del Camión</DialogTitle>
          </DialogHeader>
          {detailRow && (
            <div className="grid gap-5">
              {(detailRow.truckDeletedAt || detailRow.truckStatus !== "active") && (
                <div className="flex items-center gap-2.5 rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-3 py-2.5">
                  <span className="size-2 rounded-full bg-red-500 shrink-0" />
                  <p className="text-sm font-medium text-red-700 dark:text-red-400">
                    {detailRow.truckDeletedAt
                      ? "Camión eliminado del sistema"
                      : `Camión inactivo · ${UNIT_STATUS_LABELS[detailRow.truckStatus ?? ""] ?? detailRow.truckStatus}`
                    }
                  </p>
                </div>
              )}

              {/* Identificación */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">N° Unidad</p>
                  <p className="font-mono font-bold text-lg mt-0.5">{detailRow.truckUnitNumber ?? "—"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Tipo</p>
                  <p className="text-base font-medium mt-0.5">{detailRow.truckTypeName || detailRow.truckTypeCode || "—"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Marca y Modelo</p>
                  <p className="text-base font-medium mt-0.5">{[detailRow.truckBrand, detailRow.truckModel].filter(Boolean).join(" ") || "—"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Año</p>
                  <p className="text-base font-medium mt-0.5">{detailRow.truckManufactureYear || "—"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Patente</p>
                  <p className="font-mono text-sm bg-muted px-2 py-1 rounded mt-1">{detailRow.truckLicensePlate || "—"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Combustible</p>
                  <p className="text-base font-medium mt-0.5 capitalize">{detailRow.truckFuelType || "—"}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-muted-foreground">N° Chasis (VIN)</p>
                  <p className="font-mono text-sm bg-muted px-2 py-1 rounded mt-1 break-all">{detailRow.truckVin || "—"}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-muted-foreground">N° Motor</p>
                  <p className="font-mono text-sm bg-muted px-2 py-1 rounded mt-1">{detailRow.truckEngineNumber || "—"}</p>
                </div>
              </div>

              {/* Specs */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Especificaciones</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-sm text-muted-foreground">Tara</p>
                    <p className="text-base font-medium mt-0.5">
                      {detailRow.truckTare != null ? `${detailRow.truckTare.toLocaleString()} kg` : "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Ejes</p>
                    <p className="text-base font-medium mt-0.5">{detailRow.truckAxles || "—"}</p>
                  </div>
                </div>
              </div>



              {/* Observaciones */}
              {detailRow.truckObservations && (
                <div>
                  <p className="text-sm text-muted-foreground">Observaciones</p>
                  <p className="text-sm mt-1 text-foreground">{detailRow.truckObservations}</p>
                </div>
              )}

            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Trailer Detail Dialog */}
      <Dialog open={detailType === "trailer" && !!detailRow} onOpenChange={(open) => { if (!open) closeDetail(); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Información del Semi</DialogTitle>
          </DialogHeader>
          {detailRow && (
            <div className="grid gap-5">
              {(detailRow.trailerDeletedAt || detailRow.trailerStatus !== "active") && (
                <div className="flex items-center gap-2.5 rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-3 py-2.5">
                  <span className="size-2 rounded-full bg-red-500 shrink-0" />
                  <p className="text-sm font-medium text-red-700 dark:text-red-400">
                    {detailRow.trailerDeletedAt
                      ? "Semi eliminado del sistema"
                      : `Semi inactivo · ${UNIT_STATUS_LABELS[detailRow.trailerStatus ?? ""] ?? detailRow.trailerStatus}`
                    }
                  </p>
                </div>
              )}

              {/* Identificación */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Patente</p>
                  <p className="font-mono text-sm bg-muted px-2 py-1 rounded mt-1">{detailRow.trailerLicensePlate || "—"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Tipo</p>
                  <p className="text-base font-medium mt-1">{detailRow.trailerTypeName || detailRow.trailerTypeCode || "—"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Marca</p>
                  <p className="text-base font-medium mt-1">{detailRow.trailerBrand || "—"}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Año</p>
                  <p className="text-base font-medium mt-1">{detailRow.trailerManufactureYear || "—"}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-muted-foreground">N° Chasis (VIN)</p>
                  <p className="font-mono text-sm bg-muted px-2 py-1 rounded mt-1 break-all">{detailRow.trailerVin || "—"}</p>
                </div>
              </div>

              {/* Dimensiones y peso */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Dimensiones y Peso</p>
                <div className="grid grid-cols-4 gap-3">
                  <div>
                    <p className="text-sm text-muted-foreground">Tara</p>
                    <p className="text-base font-medium mt-0.5">
                      {detailRow.trailerTare != null ? `${detailRow.trailerTare.toLocaleString()} kg` : "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Largo</p>
                    <p className="text-base font-medium mt-0.5">{detailRow.trailerLengthM != null ? `${detailRow.trailerLengthM} m` : "—"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Ancho</p>
                    <p className="text-base font-medium mt-0.5">{detailRow.trailerWidthM != null ? `${detailRow.trailerWidthM} m` : "—"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Alto</p>
                    <p className="text-base font-medium mt-0.5">{detailRow.trailerHeightM != null ? `${detailRow.trailerHeightM} m` : "—"}</p>
                  </div>
                </div>
              </div>

              {/* Equipo de frío */}
              {detailRow.trailerRefrigerationUnitCode && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Equipo de Frío</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-sm text-muted-foreground">Código</p>
                      <p className="font-mono text-sm bg-muted px-2 py-1 rounded mt-1">{detailRow.trailerRefrigerationUnitCode}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Nombre</p>
                      <p className="text-base font-medium mt-1">{detailRow.trailerRefrigerationUnitName || "—"}</p>
                    </div>
                    {(detailRow.trailerTempSensorCount ?? 0) > 0 && (
                      <div>
                        <p className="text-sm text-muted-foreground">Sensores de Temp.</p>
                        <p className="text-base font-medium mt-1">{detailRow.trailerTempSensorCount}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Estado y extras */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Estado</p>
                <div className="flex flex-wrap gap-3">
                  <div className="flex items-center gap-1.5">
                    <span className={`size-2 rounded-full ${detailRow.trailerIsMapped ? "bg-emerald-500" : "bg-muted-foreground/40"}`} />
                    <span className="text-sm">Mapeo</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className={`size-2 rounded-full ${detailRow.trailerIsCalibrated ? "bg-emerald-500" : "bg-muted-foreground/40"}`} />
                    <span className="text-sm">Calibrado</span>
                  </div>
                  {(detailRow.trailerSealCount ?? 0) > 0 && (
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm text-muted-foreground">Precintos:</span>
                      <span className="text-sm font-medium">{detailRow.trailerSealCount}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Observaciones */}
              {detailRow.trailerObservations && (
                <div>
                  <p className="text-sm text-muted-foreground">Observaciones</p>
                  <p className="text-sm mt-1 text-foreground">{detailRow.trailerObservations}</p>
                </div>
              )}

            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Refrigeration Unit Detail Dialog */}
      <Dialog open={detailType === "refrig" && !!detailRow} onOpenChange={(open) => { if (!open) closeDetail(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Equipo de Frío</DialogTitle>
          </DialogHeader>
          {detailRow && (
            <div className="grid gap-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Código Interno</p>
                  <p className="font-mono text-sm bg-muted px-2 py-1 rounded mt-1">{detailRow.trailerRefrigerationUnitCode || "—"}</p>
                </div>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Descripción</p>
                <p className="text-base font-medium mt-1">{detailRow.trailerRefrigerationUnitName || "—"}</p>
              </div>
              {detailRow.trailerRefrigerationUnitObservations && (
                <div>
                  <p className="text-sm text-muted-foreground">Observaciones</p>
                  <p className="text-sm mt-1 text-foreground">{detailRow.trailerRefrigerationUnitObservations}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Share Dialog ─────────────────────────────────────────────────────── */}
      <Dialog open={!!shareRow} onOpenChange={(open) => { if (!open) setShareRow(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2Icon className="size-4" />
              Compartir móvil
            </DialogTitle>
            <DialogDescription>
              {shareRow?.driverName ?? "Móvil"}
              {shareRow?.truckUnitNumber != null ? ` • M${shareRow.truckUnitNumber}` : ""}
            </DialogDescription>
          </DialogHeader>

          {shareRow && (() => {
            const text = buildShareText(shareRow);
            const waUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
            return (
              <div className="flex flex-col gap-3">
                <pre className="whitespace-pre-wrap text-sm bg-muted rounded-md px-4 py-3 font-sans leading-relaxed max-h-72 overflow-y-auto">
                  {text}
                </pre>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    className="flex-1 gap-2"
                    onClick={() => handleCopy(text)}
                  >
                    {copied
                      ? <><CheckIcon className="size-4 text-emerald-500" /> Copiado</>
                      : <><CopyIcon className="size-4" /> Copiar texto</>}
                  </Button>
                  <Button
                    className="flex-1 gap-2 bg-[#25D366] hover:bg-[#1ebe59] text-white"
                    onClick={() => window.open(waUrl, "_blank")}
                  >
                    <svg viewBox="0 0 24 24" className="size-4 fill-current" xmlns="http://www.w3.org/2000/svg">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                      <path d="M12 0C5.373 0 0 5.373 0 12c0 2.125.554 4.118 1.524 5.847L.057 23.882l6.198-1.625A11.93 11.93 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.793 9.793 0 0 1-5.001-1.376l-.36-.213-3.681.965.982-3.588-.233-.369A9.79 9.79 0 0 1 2.182 12C2.182 6.57 6.57 2.182 12 2.182S21.818 6.57 21.818 12 17.43 21.818 12 21.818z"/>
                    </svg>
                    WhatsApp
                  </Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* ── Delete Dialog ────────────────────────────────────────────────────── */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Eliminar móvil</DialogTitle>
            <DialogDescription>
              ¿Eliminás el móvil de <strong>{deleting?.driverName ?? "este chofer"}</strong>? Esta acción es reversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose>
              <Button variant="outline" onClick={() => setDeleting(null)}>Cancelar</Button>
            </DialogClose>
            <Button
              variant="destructive"
              disabled={deleteMut.isPending}
              onClick={() => deleting && deleteMut.mutate(deleting.id)}
            >
              {deleteMut.isPending ? "Eliminando..." : "Eliminar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Reassign Confirmation Dialog ──────────────────────────────────────── */}
      <Dialog open={!!reassignPreview} onOpenChange={(open) => { if (!open) cancelReassign(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirmar reasignación</DialogTitle>
            <DialogDescription>
              Los siguientes recursos están asignados a otras asignaciones. Al guardar se producirán estos cambios automáticamente:
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            {reassignPreview?.map((conflict, i) => {
              const fieldLabel = conflict.field === "driver" ? "Chofer" : conflict.field === "truck" ? "Camión" : "Trailer";
              return (
                <div key={i} className="rounded-md border bg-muted/40 px-3 py-2.5 space-y-1">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <span className="text-primary">{fieldLabel}: {conflict.resourceLabel}</span>
                  </div>
                  <ul className="text-xs text-muted-foreground space-y-0.5 list-disc list-inside">
                    <li>Se reasignará a esta asignación</li>
                    <li>Se desasignará de: <span className="font-medium text-foreground">{conflict.fromMobileLabel}</span></li>
                    {conflict.activeTripsCount > 0 && (
                      <li className="text-amber-600 font-medium">
                        ⚠️ Esa asignación tiene {conflict.activeTripsCount} viaje{conflict.activeTripsCount !== 1 ? "s" : ""} activo{conflict.activeTripsCount !== 1 ? "s" : ""} (no se modificarán)
                      </li>
                    )}
                  </ul>
                </div>
              );
            })}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={cancelReassign}>Cancelar</Button>
            <Button onClick={confirmReassign} disabled={updateMut.isPending}>
              {updateMut.isPending ? "Guardando..." : "Confirmar y guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
